// vue
import { markRaw, shallowRef } from 'vue'
// libraries
import { MeiliSearch } from 'meilisearch'
// utilities
import { debounce } from '@/utilities/debounce'

/**
 * Context passed to the Meilisearch executor.
 */
export type MeilisearchSearchContext = {
  /**
   * Initialized Meilisearch client.
   */
  client: MeiliSearch
  /**
   * Abort signal for the current request.
   */
  signal: AbortSignal
  /**
   * Search query.
   */
  query: string
}

/**
 * Options for controlling Meilisearch search behavior.
 */
export type MeilisearchSearchOptions<T> = {
  /**
   * Debounce delay in milliseconds.
   */
  debounceMs?: number
  /**
   * Minimum query length before searching.
   */
  minQueryLength?: number
  /**
   * Optional flag to skip searches when inactive.
   */
  isActive?: () => boolean
  /**
   * Called when the search should be cleared.
   */
  onClear?: () => void
  /**
   * Called with results for the latest request.
   */
  onResult?: (result: T) => void
  /**
   * Optional error handler.
   */
  onError?: (error: unknown) => void
}

/**
 * Search handle returned by createSearch.
 */
export type MeilisearchSearchHandle = {
  /**
   * Schedules a debounced search.
   */
  scheduleSearch: (query: string) => void
  /**
   * Runs a search immediately.
   */
  runSearch: (query: string) => Promise<void>
  /**
   * Cancels any in-flight or pending search.
   */
  cancelSearch: () => void
  /**
   * Disposes the search handler and client.
   */
  dispose: () => void
  /**
   * Returns the current client instance.
   */
  getClient: () => MeiliSearch | null
}

/**
 * Composable to manage Meilisearch client initialization, debouncing, and cancellation.
 */
export default function useMeilisearch() {
  /**
   * Meilisearch client instance stored as a shallow ref and marked raw to avoid Vue proxying.
   */
  const client = shallowRef<MeiliSearch | null>(null)

  /**
   * Initializes the Meilisearch client when needed.
   */
  const initClient = (): void => {
    if (!client.value) {
      client.value = markRaw(
        new MeiliSearch({
          host: import.meta.env.VITE_MEILISEARCH_URL,
          apiKey: import.meta.env.VITE_MEILISEARCH_API_KEY
        })
      )
    }
  }

  /**
   * Creates a debounced and cancellable search handler.
   * @param executor - Search executor that returns a result
   * @param options - Search behavior options
   */
  const createSearch = <T>(
    executor: (context: MeilisearchSearchContext) => Promise<T>,
    options: MeilisearchSearchOptions<T> = {}
  ): MeilisearchSearchHandle => {
    /**
     * Active abort controller for the current request.
     */
    let searchAbortController: AbortController | null = null
    /**
     * Latest search request id for stale response protection.
     */
    let searchRequestId = 0

    /**
     * Cancels any in-flight search request.
     */
    const abortActiveSearch = (): void => {
      if (searchAbortController) {
        searchAbortController.abort()
        searchAbortController = null
      }
    }

    /**
     * Clears pending and active searches.
     */
    const cancelSearch = (): void => {
      abortActiveSearch()
      debouncedSearch.cancel()
    }

    /**
     * Runs a search with cancellation and stale-response handling.
     * @param query - Search query
     */
    const runSearch = async (query: string): Promise<void> => {
      cancelSearch()
      searchRequestId += 1
      const requestId = searchRequestId

      const trimmedQuery = query?.trim() ?? ''
      const minQueryLength = options.minQueryLength ?? 1
      const isActive = options.isActive ? options.isActive() : true

      if (!isActive || trimmedQuery.length < minQueryLength) {
        options.onClear?.()
        return
      }

      initClient()

      if (!client.value) {
        options.onClear?.()
        return
      }

      const abortController = new AbortController()
      searchAbortController = abortController

      try {
        const result = await executor({
          client: client.value,
          signal: abortController.signal,
          query: trimmedQuery
        })

        if (requestId !== searchRequestId) {
          return
        }

        options.onResult?.(result)
      } catch (error) {
        if (abortController.signal.aborted) {
          return
        }
        options.onError?.(error)
        console.error('Meilisearch query failed:', error)
      } finally {
        if (searchAbortController === abortController) {
          searchAbortController = null
        }
      }
    }

    /**
     * Debounced search wrapper.
     */
    const debouncedSearch = debounce((searchQuery: string) => {
      void runSearch(searchQuery)
    }, options.debounceMs ?? 300)

    /**
     * Schedules a debounced search.
     */
    const scheduleSearch = (query: string): void => {
      debouncedSearch(query)
    }

    /**
     * Disposes the search handler and client.
     */
    const dispose = (): void => {
      cancelSearch()
      client.value = null
    }

    return {
      scheduleSearch,
      runSearch,
      cancelSearch,
      dispose,
      getClient: () => client.value
    }
  }

  return {
    createSearch,
    initClient,
    getClient: () => client.value
  }
}
